/**
 * Product-page descriptor recipes — the route-facing constructors for
 * `FmProductPage`: one call per chapter, one call per repeated section shape.
 * Recipes return sealed typed descriptors only; they never contain layout, CSS,
 * tokens, `ReactNode`, children, or media URLs. The archetype owns all of that.
 */
import type {
  FmProductActionDescriptor,
  FmProductChapterDescriptor,
  FmProductSectionDescriptor,
  FmProductSectionRole,
  FmProductSharedSection,
} from './FmProductPage.types';
import type { FmStoryChartDataValue, FmStoryChartSeries } from './elements/elementFields.types';

/** Compose one owner chapter from its ordered sections. */
export function productChapter(input: {
  id: string;
  order: number;
  title: string;
  answer: string;
  dominantSectionId: string;
  conversionRole: FmProductChapterDescriptor['conversionRole'];
  transitionLabel: string;
  targetChapterId?: string;
  sections: readonly [FmProductSectionDescriptor, ...FmProductSectionDescriptor[]];
}): FmProductChapterDescriptor {
  return {
    id: input.id,
    ownerQuestion: input.title,
    literalAnswer: input.answer,
    dominantSectionId: input.dominantSectionId,
    conversionRole: input.conversionRole,
    mobileOrder: input.order,
    transition: {
      label: input.transitionLabel,
      ...(input.targetChapterId ? { targetChapterId: input.targetChapterId } : {}),
    },
    sections: input.sections,
  };
}

/** The chapter's action rail: the answer, why it applies now, and the moves. */
export function productActionsSection(input: {
  id: string;
  title: string;
  answer: string;
  context: string;
  actions: readonly [FmProductActionDescriptor, ...FmProductActionDescriptor[]];
  role?: FmProductSectionRole;
}): FmProductSectionDescriptor {
  return {
    id: input.id,
    capability: 'contextual-actions',
    role: input.role ?? 'action',
    title: input.title,
    state: { status: 'ready' },
    answer: input.answer,
    context: input.context,
    actions: input.actions,
  };
}

/**
 * A dated series read as one chart under its answer: copy (heading and
 * summary) then the chart element, in a prose section.
 */
export function productTimeSeriesChartSection(input: {
  id: string;
  sectionId: string;
  title: string;
  summary: string;
  locale: string;
  chartTitle: string;
  caption: string;
  unit: string;
  takeaway: string;
  source: string;
  xKey: string;
  series: readonly [FmStoryChartSeries, ...FmStoryChartSeries[]];
  data: readonly Record<string, FmStoryChartDataValue>[];
  testId: string;
  role?: FmProductSectionRole;
}): FmProductSharedSection {
  return {
    id: input.id,
    capability: 'section',
    role: input.role ?? 'lead',
    title: input.title,
    section: {
      id: input.sectionId,
      arrangement: 'prose',
      state: { status: 'ready' },
      testId: input.sectionId,
      slots: {
        content: [
          { element: 'copy', heading: input.title, lede: input.summary },
          {
            element: 'chart',
            locale: input.locale,
            title: input.chartTitle,
            caption: input.caption,
            unit: input.unit,
            takeaway: input.takeaway,
            source: input.source,
            chart: {
              kind: 'time-series',
              xKey: input.xKey,
              legend: 'none',
              series: input.series,
              data: input.data,
            },
            testId: input.testId,
          },
        ],
      },
    },
  };
}
